/**
 * Content loader utility for Netlify CMS Git-based content
 * Loads markdown and JSON files from the content directory
 */

/**
 * Parse frontmatter from markdown content
 * @param {string} markdown - Raw markdown with frontmatter
 * @returns {object} - Parsed frontmatter and content
 */
export function parseFrontmatter(markdown) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = markdown.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content: markdown };
  }

  const [, frontmatterStr, content] = match;
  const data = {};

  // Parse YAML-like frontmatter
  frontmatterStr.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Remove quotes
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Handle arrays
      if (value.startsWith('[') && value.endsWith(']')) {
        value = value
          .slice(1, -1)
          .split(',')
          .map(v => v.trim().replace(/['"]/g, ''));
      }

      data[key] = value;
    }
  });

  return { data, content: content.trim() };
}

/**
 * Load all blog posts
 * @returns {Promise<Array>} - Array of blog post objects
 */
export async function loadBlogPosts() {
  try {
    // Use Vite's glob import for static site generation
    const blogFiles = import.meta.glob('/src/content/blog/*.md', { as: 'raw', eager: true });
    const posts = [];

    for (const [path, content] of Object.entries(blogFiles)) {
      const { data, content: body } = parseFrontmatter(content);
      const filename = path.split('/').pop().replace('.md', '');

      posts.push({
        slug: data.slug || filename,
        ...data,
        body,
        filename,
      });
    }

    // Sort by date (newest first)
    return posts
      .filter(post => post.status === 'published')
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    console.error('Error loading blog posts:', error);
    return [];
  }
}

/**
 * Load a single blog post by slug
 * @param {string} slug - Post slug
 * @returns {Promise<object|null>} - Blog post object or null
 */
export async function loadBlogPost(slug) {
  try {
    const posts = await loadBlogPosts();
    return posts.find(post => post.slug === slug) || null;
  } catch (error) {
    console.error('Error loading blog post:', error);
    return null;
  }
}

/**
 * Load a page by slug
 * @param {string} slug - Page slug
 * @returns {Promise<object|null>} - Page object or null
 */
export async function loadPage(slug) {
  try {
    const pageFiles = import.meta.glob('/src/content/pages/*.md', { as: 'raw', eager: true });

    for (const [path, content] of Object.entries(pageFiles)) {
      const { data, content: body } = parseFrontmatter(content);
      if (data.slug === slug && data.status === 'published') {
        return { ...data, body };
      }
    }

    return null;
  } catch (error) {
    console.error('Error loading page:', error);
    return null;
  }
}

/**
 * Load site settings
 * @returns {Promise<object>} - Site settings object
 */
export async function loadSiteSettings() {
  try {
    const settings = await import('/src/content/settings/site.json');
    return settings.default || settings;
  } catch (error) {
    console.error('Error loading site settings:', error);
    return {
      siteTitle: 'Shakes Travel',
      siteDescription: 'Uganda Safari Adventures',
      contact: {},
      social: {},
    };
  }
}

/**
 * Load navigation menu
 * @returns {Promise<Array>} - Navigation items
 */
export async function loadNavigation() {
  try {
    const nav = await import('/src/content/settings/navigation.json');
    const navData = nav.default || nav;
    return navData.mainMenu.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error loading navigation:', error);
    return [];
  }
}

/**
 * Convert markdown to HTML (simple version)
 * For production, consider using a library like marked or remark
 * @param {string} markdown - Markdown string
 * @returns {string} - HTML string
 */
export function markdownToHtml(markdown) {
  if (!markdown) return '';

  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" />');

  // Line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap in paragraphs
  html = `<p>${html}</p>`;

  return html;
}

/**
 * Get blog posts by category
 * @param {string} category - Category name
 * @returns {Promise<Array>} - Filtered blog posts
 */
export async function loadBlogPostsByCategory(category) {
  const posts = await loadBlogPosts();
  return posts.filter(
    post => post.categories && post.categories.includes(category)
  );
}

/**
 * Get blog posts by tag
 * @param {string} tag - Tag name
 * @returns {Promise<Array>} - Filtered blog posts
 */
export async function loadBlogPostsByTag(tag) {
  const posts = await loadBlogPosts();
  return posts.filter(post => post.tags && post.tags.includes(tag));
}

/**
 * Get all unique categories
 * @returns {Promise<Array>} - Array of category names
 */
export async function loadCategories() {
  const posts = await loadBlogPosts();
  const categories = new Set();

  posts.forEach(post => {
    if (post.categories) {
      post.categories.forEach(cat => categories.add(cat));
    }
  });

  return Array.from(categories).sort();
}

/**
 * Get all unique tags
 * @returns {Promise<Array>} - Array of tag names
 */
export async function loadTags() {
  const posts = await loadBlogPosts();
  const tags = new Set();

  posts.forEach(post => {
    if (post.tags) {
      post.tags.forEach(tag => tags.add(tag));
    }
  });

  return Array.from(tags).sort();
}
